import React, { useContext, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { Check, Lock, Upload, X, FileText, Video } from 'lucide-react';
import { Context } from 'Context/Context';
import { getCaseDetailsApi } from 'Constants';
import useCaseDocuments from '../CaseDetails/useCaseDocuments';

// ---------------------------------------------------------------------------
// Right after a client publishes a case: a small two-step popup.
//   1. Add supporting documents (optional) -> Next
//   2. Add a video statement (optional)    -> Back / View published case
// One button per step (it works whether or not anything was uploaded).
// Closing the popup (x) also goes to the case details page.
// ---------------------------------------------------------------------------
const STEPS = {
  1: {
    name: 'supportingDocuments',
    title: 'Add supporting documents',
    line: 'Photos, medical records, police reports. You can also add these later from your case page.',
    choose: 'Choose files',
    hint: 'PDF, Word or images · up to 10 files, 10MB each',
    accept: 'image/*,.doc,.docx,.pdf',
    multiple: true,
    privacy: 'Only law firms you approve can see these.',
    Icon: FileText,
  },
  2: {
    name: 'videoDocument',
    title: 'Add a video statement',
    line: 'A short video telling your story in your own words. You can also add it later from your case page.',
    choose: 'Choose a video',
    hint: 'MP4 · one video, up to 150MB',
    accept: '.mp4',
    multiple: false,
    privacy: 'Only law firms you approve can see this.',
    Icon: Video,
  },
};

function CaseCreatedUpload({ caseId }) {
  const navigate = useNavigate();
  const { makeApiCallFunc } = useContext(Context);
  const [step, setStep] = useState(1);
  const [caseDetails, setCaseDetails] = useState({ _id: caseId, supportingDocuments: [], videoDocument: [] });
  const [reload, setReload] = useState(0);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    makeApiCallFunc(getCaseDetailsApi + '/' + caseId, null,
      ({ data }) => { if (data && !data.is_deleted) setCaseDetails(data); },
      (err) => console.log(err), null, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, reload]);

  const { uploadDocuments, removeDoc, uploading } = useCaseDocuments(caseId, caseDetails, () => setReload((n) => n + 1));
  const s = STEPS[step];
  const files = caseDetails?.[s.name] || [];
  const busy = !!uploading;

  // while a file is uploading, refreshing or closing the tab would cancel it -
  // ask the browser to show its "Leave site?" warning
  useEffect(() => {
    if (!busy) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);
  const goToCase = () => { if (!busy) navigate('/client/caseDetails/' + caseId); };

  const remove = (fileName) => {
    setRemoving(fileName);
    removeDoc(fileName, { [s.name]: files.filter((f) => f.fileName !== fileName) }, () => setRemoving(null));
  };

  const inputId = 'case-created-' + s.name;
  const picker = (
    <input type="file" id={inputId} className="hidden" accept={s.accept} multiple={s.multiple}
      onChange={(e) => uploadDocuments(e, s.name)} disabled={busy} />
  );
  const { Icon } = s;

  return (
    <ReactModal
      isOpen
      onRequestClose={goToCase}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={!busy}
      style={{
        overlay: { zIndex: 40, backgroundColor: 'rgba(20,33,61,.45)' },
        content: {
          top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)',
          width: 'calc(100vw - 32px)', maxWidth: '480px', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
          padding: 0, border: 'none', borderRadius: '12px', backgroundColor: '#fffdf8',
          boxShadow: '0 24px 60px -20px rgba(20,33,61,.55)',
        },
      }}
    >
      <div className="font-plexsans text-navy px-[18px] pt-[22px] pb-[18px] sm:px-[26px] sm:pt-[26px] sm:pb-[22px]">
        <div className="flex items-center justify-between gap-3 mb-[18px]">
          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold leading-none text-success bg-success/[.08] border border-success/[.25] rounded-[20px] py-[5px] px-2.5">
            <Check className="w-3 h-3" strokeWidth={2.8} aria-hidden="true" />Case published
          </span>
          <div className="flex items-center gap-3">
            <span className="flex gap-1.5" aria-label={`Part ${step} of 2`}>
              <span className={`w-[22px] h-1 rounded-sm ${step >= 1 ? 'bg-brass' : 'bg-navy/[.12]'}`} />
              <span className={`w-[22px] h-1 rounded-sm ${step >= 2 ? 'bg-brass' : 'bg-navy/[.12]'}`} />
            </span>
            <button type="button" onClick={goToCase} disabled={busy} aria-label="Close and go to your case"
              className="flex items-center justify-center w-8 h-8 -mr-1.5 rounded-md bg-transparent border-0 text-navy/[.55] hover:text-navy hover:bg-navy/[.05] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
              <X className="w-[18px] h-[18px]" strokeWidth={2.2} />
            </button>
          </div>
        </div>

        <h2 className="m-0 mb-1.5 font-spectral font-medium text-[23px] leading-[1.3] text-navy">
          {s.title} <span className="font-plexsans text-[15px] font-medium text-navy/[.5]">(optional)</span>
        </h2>
        <p className="m-0 mb-4 text-[13.5px] leading-[1.55] text-navy/[.62]">{s.line}</p>

        {picker}
        {files.length === 0 ? (
          <div className="border-[1.5px] border-dashed border-brass/[.45] bg-brass/[.04] rounded-[10px] py-[26px] px-4 text-center">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brass/[.1] mb-2.5">
              <Upload className="w-[18px] h-[18px] text-brass" strokeWidth={2} aria-hidden="true" />
            </span>
            <div>
              {busy
                ? <span className="inline-block text-[13.5px] font-semibold text-brass py-2.5" role="status">Uploading…</span>
                : <label htmlFor={inputId} className="inline-block bg-white border border-navy/[.2] rounded-lg py-2.5 px-4 font-semibold text-[13.5px] text-navy cursor-pointer hover:border-brass">{s.choose}</label>}
            </div>
            <div className="text-[12px] text-navy/[.55] mt-2">{s.hint}</div>
          </div>
        ) : (
          <>
            <ul className="m-0 mb-2.5 p-0 list-none border border-navy/[.12] rounded-[10px] bg-white">
              {files.map((f) => (
                <li key={f.fileName} className="flex items-center gap-2.5 py-2.5 px-3 border-b border-navy/[.08] last:border-b-0 text-[13.5px]">
                  <Icon className="w-4 h-4 text-brass flex-none" strokeWidth={2} aria-hidden="true" />
                  <span className="flex-1 min-w-0 truncate">{f.fileName}</span>
                  <button type="button" onClick={() => remove(f.fileName)} disabled={busy || removing === f.fileName}
                    aria-label={`Remove ${f.fileName}`}
                    className="flex items-center justify-center w-7 h-7 rounded bg-transparent border-0 text-navy/[.45] hover:text-danger cursor-pointer disabled:opacity-40">
                    <X className="w-4 h-4" strokeWidth={2.2} />
                  </button>
                </li>
              ))}
            </ul>
            {busy
              ? <span className="text-[13px] font-semibold text-brass" role="status">Uploading…</span>
              : s.multiple && files.length < 10 &&
                <label htmlFor={inputId} className="text-[13px] font-semibold text-brass cursor-pointer hover:text-brassDark">+ Add more files</label>}
          </>
        )}

        <p className="m-0 mt-3.5 flex items-center gap-[7px] text-[12px] text-navy/[.58]">
          <Lock className="w-[13px] h-[13px] flex-none" strokeWidth={2.2} aria-hidden="true" />{s.privacy}
        </p>

        <div className={`flex mt-[22px] gap-2.5 ${step === 2 ? 'justify-between' : 'justify-end'} max-sm:flex-col-reverse max-sm:items-stretch`}>
          {step === 2 &&
            <button type="button" onClick={() => setStep(1)} disabled={busy}
              className="bg-transparent border-0 font-semibold text-[13.5px] text-navy/[.62] hover:text-navy py-3 px-1 cursor-pointer disabled:opacity-40">
              ← Back
            </button>}
          <button type="button" onClick={step === 1 ? () => setStep(2) : goToCase} disabled={busy}
            className="inline-flex items-center justify-center gap-1.5 h-[46px] sm:h-auto py-3 px-5 rounded-lg border-0 bg-brass hover:bg-brassDark text-ivory font-semibold text-[13.5px] cursor-pointer disabled:opacity-50 disabled:cursor-wait">
            {step === 1 ? 'Next →' : 'View published case →'}
          </button>
        </div>
        {busy && <p className="m-0 mt-2 text-right max-sm:text-center text-[12px] text-navy/[.55]">Please wait until the upload finishes.</p>}
      </div>
    </ReactModal>
  );
}

export default CaseCreatedUpload;
